import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaUndoAlt } from 'react-icons/fa';

const RefundPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="container mx-auto px-4 py-4">
                    <Link to="/" className="inline-flex items-center text-pink-600 hover:text-pink-700 font-semibold transition-colors">
                        <FaArrowLeft className="mr-2" />
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 max-w-4xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                    {/* Title */}
                    <div className="flex items-center mb-8">
                        <FaUndoAlt className="text-4xl text-pink-600 mr-4" />
                        <div>
                            <h1 className="text-4xl font-black text-gray-900">Refund Policy</h1>
                            <p className="text-gray-600 mt-1">Last updated: December 16, 2025</p>
                        </div>
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8 text-gray-700">
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. General Policy</h2>
                            <p className="leading-relaxed">
                                At SoulMate, we strive to provide the best possible experience. All premium membership fees are generally non-refundable, except in specific circumstances outlined below or where required by applicable law.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Cooling-Off Period</h2>
                            <p className="leading-relaxed mb-3">
                                We offer a 14-day cooling-off period for new premium membership purchases. If you purchase a subscription and change your mind within 14 days of payment, you may request a full refund provided:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>You have not actively used premium features (e.g., sent unlimited messages, viewed contact details, or downloaded reports)</li>
                                <li>The request is made within 14 calendar days from the date of successful payment</li>
                                <li>This is your first subscription purchase on SoulMate</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligible Refund Scenarios</h2>
                            <p className="leading-relaxed mb-3">
                                Refunds may be issued in the following cases:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Technical issues: If you experience persistent technical problems that prevent access to paid features and we are unable to resolve them within 7 days</li>
                                <li>Duplicate charges: Accidental multiple payments for the same subscription</li>
                                <li>Unauthorized transactions: Charges made without your consent (subject to verification)</li>
                                <li>Service not provided: If we fail to deliver the promised premium services</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Non-Refundable Situations</h2>
                            <p className="leading-relaxed mb-3">
                                No refunds will be provided for:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Change of mind after the 14-day cooling-off period</li>
                                <li>Partial use of the subscription period</li>
                                <li>Subscriptions renewed automatically</li>
                                <li>Discounted or promotional plans</li>
                                <li>Any usage of premium features beyond basic viewing</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Refund Process</h2>
                            <p className="leading-relaxed mb-3">
                                To request a refund:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Contact our support team within the eligible timeframe</li>
                                <li>Provide your registered email, transaction ID, and reason for refund</li>
                                <li>We will review your request within 3-5 business days</li>
                                <li>Approved refunds will be processed to your original payment method within 7-10 business days</li>
                            </ul>
                            <p className="leading-relaxed mt-4">
                                Note: Refund timelines may vary depending on your payment provider or bank.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cancellation of Subscription</h2>
                            <p className="leading-relaxed">
                                You can cancel auto-renewal at any time from your account settings. Cancellation prevents future charges but does not entitle you to a refund for the current subscription period (except under the cooling-off period).
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Changes to This Policy</h2>
                            <p className="leading-relaxed">
                                We may update this Refund Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date. Continued use of the service after changes constitutes acceptance of the revised policy.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h2>
                            <p className="leading-relaxed">
                                For any refund-related queries or requests, please reach out to us at:
                            </p>
                            <div className="mt-4 p-4 bg-pink-50 rounded-lg">
                                <p className="font-semibold">Email: info@softyetech.com</p>
                                <p className="font-semibold">Phone: +91 98765 43210</p>
                                <p className="font-semibold">Address: 573, Softye Technologies, Mettur main road, Ammapettai - Erode</p>
                            </div>
                        </section>
                    </div>

                    {/* Footer */}
                    <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                        <p className="text-gray-600">
                            Your satisfaction is important to us at SoulMate.
                        </p>
                        <Link to="/" className="inline-block mt-4 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RefundPolicy;